import { useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router';
import clsx from 'clsx';

import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import { getRedirectPath } from '@flow/libs/workspace/folders';
import { getLocationSelector } from '@app/reducers/router';
import foldersReducer from '@app/reducers/entities/folders';

import { mapToLink } from '@app/components/bread-crumb/helper';
import { FolderBreadCrumb } from '@app/components/bread-crumb/folder/folder-bread-crumb';

import style from './folder-bread-crumb-container.module.scss';

type BreadCrumbType = {
  id: number;
  name: string;
  to: string;
};

export const getCurrentWorkspaceName = (currentWorkspace: Oneflow.Workspace) => {
  if (!currentWorkspace) {
    return '';
  }

  return currentWorkspace.name;
};

type Props = {
  selectedUniqueKey?: any;
};

export const FolderBreadCrumbContainer = ({ selectedUniqueKey }: Props) => {
  const currentWorkspace = useSelector(getCurrentWorkspaceSelector) as Oneflow.Workspace;
  const currentFolderSelector = useSelector((state) =>
    foldersReducer.getFetchSelector(state, {
      id: selectedUniqueKey,
    }),
  );
  const currentFolder = useSelector((state) =>
    foldersReducer.getFolderSelector(state, {
      id: currentFolderSelector.id,
    }),
  ) as Oneflow.Folder;

  const history = useHistory();
  const location = useSelector(getLocationSelector);

  const renderBreadCrumb = () => {
    if (isEmpty(currentFolder)) {
      return null;
    }

    const parentFolders = Object.values(currentFolder.resolvedPath) as Oneflow.Folder[];

    const breadCrumbs: BreadCrumbType[] & Oneflow.Folder[] = [
      {
        id: -1,
        name: getCurrentWorkspaceName(currentWorkspace),
        to: '',
      },
    ];

    const parentPath =
      currentFolder.parentPath
        ?.split('/')
        .slice(0, -1)
        .map((path) => Number(path)) ?? [];

    parentPath.forEach((parentFolderId) => {
      const parentFolder = parentFolders.find((folder) => folder.id === parentFolderId);
      if (parentFolder) {
        breadCrumbs.push(parentFolder);
      }
    });

    breadCrumbs.push(currentFolder);

    return (
      <FolderBreadCrumb
        links={breadCrumbs.map(mapToLink)}
        onBreadCrumbItemClick={(id) => {
          if (id) {
            history.push({
              search: getRedirectPath(location, id, true),
            });
          }
        }}
      />
    );
  };

  if (selectedUniqueKey === -1) {
    return null;
  }

  const breadCrumb = renderBreadCrumb();

  if (!breadCrumb) {
    return null;
  }

  return (
    <div
      className={clsx(style.Header, style.NewDesign)}
      data-testid="folder-bread-crumb"
    >
      <ul className={style.BreadCrumb}>{breadCrumb}</ul>
    </div>
  );
};
